import React, { useEffect, useState, useCallback } from 'react';
import { Users, UserPlus, Edit2, Trash2, AlertCircle, CheckCircle2, XCircle, Search, Filter, Mail, Phone } from 'lucide-react';
import { Plus } from 'lucide-react';
import ServiceContainer from '../../../core/services/ServiceContainer';
import { useAuthStore } from '../../../core/services/AuthStore';
import { useUserStore } from '../../../core/services/UserStore';
import { useClientStore } from '../../../core/services/ClientStore';
import { useDamStore } from '../../../core/services/DamStore';
import { IUser } from '../../../core/interfaces/IUser';
import { Card, CardContent } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import { Input } from '../../../shared/components/ui/input';
import { Label } from '../../../shared/components/ui/label';
import { Badge } from '../../../shared/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../shared/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../shared/components/ui/select';

export const UsersPage: React.FC = () => {
  const MAX_USER_PHONE_NUMBERS = 3;

  const { user: currentUser } = useAuthStore();
  const { users, loading: usersLoading, error, setUsers, setLoading, setError, removeUser } = useUserStore();
  const { clients, setClients } = useClientStore();
  const { dams, setDams } = useDamStore();
  
  const serviceContainer = ServiceContainer.getInstance();
  const userService = serviceContainer.getUserService();
  const clientService = serviceContainer.getClientService();
  const damService = serviceContainer.getDamService();

  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<IUser | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterClientId, setFilterClientId] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<IUser | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phones: [] as string[],
    clientId: '',
    roleName: 'colaborador',
    damIds: [] as string[],
  });
  const [phoneInput, setPhoneInput] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('+55');

  const countries = [
    { code: '+55', name: 'Brasil', iso: 'br', placeholder: '11987654321' },
    { code: '+1', name: 'Estados Unidos', iso: 'us', placeholder: '2025551234' },
    { code: '+351', name: 'Portugal', iso: 'pt', placeholder: '912345678' },
    { code: '+34', name: 'Espanha', iso: 'es', placeholder: '612345678' },
    { code: '+33', name: 'França', iso: 'fr', placeholder: '612345678' },
    { code: '+49', name: 'Alemanha', iso: 'de', placeholder: '15212345678' },
    { code: '+44', name: 'Reino Unido', iso: 'gb', placeholder: '7400123456' },
    { code: '+39', name: 'Itália', iso: 'it', placeholder: '3123456789' },
    { code: '+52', name: 'México', iso: 'mx', placeholder: '5512345678' },
    { code: '+54', name: 'Argentina', iso: 'ar', placeholder: '91123456789' },
  ];

  const CountryFlag = ({ iso, className = '' }: { iso: string; className?: string }) => (
    <img
      src={`https://flagcdn.com/w20/${iso}.png`}
      srcSet={`https://flagcdn.com/w40/${iso}.png 2x`}
      width="20"
      height="15"
      alt={iso.toUpperCase()}
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    />
  );

  const validateInternationalPhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[\s\-()]/g, '');

    if (cleanPhone.startsWith('+55')) {
      const brazilMobileRegex = /^\+55\d{2}9\d{8}$/;
      if (!brazilMobileRegex.test(cleanPhone)) {
        return false;
      }

      const ddd = parseInt(cleanPhone.substring(3, 5));
      const validDDDs = [
        11, 12, 13, 14, 15, 16, 17, 18, 19,
        21, 22, 24, 27, 28,
        31, 32, 33, 34, 35, 37, 38,
        41, 42, 43, 44, 45, 46,
        47, 48, 49,
        51, 53, 54, 55,
        61, 62, 64, 63,
        65, 66, 67, 68, 69,
        71, 73, 74, 75, 77, 79,
        81, 87, 82, 83, 84,
        85, 88, 86, 89,
        91, 93, 94, 92, 97,
        95, 96, 98, 99,
      ];

      return validDDDs.includes(ddd);
    }

    const internationalPhoneRegex = /^\+\d{1,3}\d{8,15}$/;
    return internationalPhoneRegex.test(cleanPhone);
  };

  const isBrazilianLandline = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[\s\-()]/g, '');

    if (!cleanPhone.startsWith('+55')) {
      return false;
    }

    const brazilPhoneRegex = /^\+55\d{2}\d{8,9}$/;
    if (!brazilPhoneRegex.test(cleanPhone)) {
      return false;
    }

    const ninthDigit = cleanPhone.charAt(5);
    return ninthDigit !== '9';
  };

  const formatPhoneNumber = (value: string, countryCode: string): string => {
    const numbers = value.replace(/\D/g, '');

    if (!numbers) return '';

    if (countryCode === '+55') {
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 7) return `${numbers.slice(0, 2)} ${numbers.slice(2)}`;
      return `${numbers.slice(0, 2)} ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }

    if (countryCode === '+1' && numbers.length >= 10) {
      return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }

    if (countryCode === '+351' && numbers.length >= 9) {
      return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6, 9)}`;
    }

    return numbers.match(/.{1,4}/g)?.join(' ') || numbers;
  };

  const parsePhoneNumber = (fullPhone: string) => {
    const country = countries.find((c) => fullPhone.startsWith(c.code));
    if (!country) return { iso: 'un', code: '', number: fullPhone };

    const number = fullPhone.substring(country.code.length);
    return { iso: country.iso, code: country.code, number: formatPhoneNumber(number, country.code) };
  };

  const handleAddPhone = () => {
    if (formData.phones.length >= MAX_USER_PHONE_NUMBERS) {
      setPhoneError(`Limite máximo de ${MAX_USER_PHONE_NUMBERS} números por usuário atingido`);
      return;
    }

    if (!phoneInput.trim()) {
      setPhoneError('Digite um número de telefone');
      return;
    }

    const cleanNumber = phoneInput.replace(/\D/g, '');
    const fullPhone = `${selectedCountryCode}${cleanNumber}`;

    if (isBrazilianLandline(fullPhone)) {
      setPhoneError('⚠️ Número fixo não é permitido. Apenas números de celular são aceitos.');
      return;
    }

    if (!validateInternationalPhone(fullPhone)) {
      if (selectedCountryCode === '+55') {
        setPhoneError('Número brasileiro inválido. Use formato: DDD + 9 + 8 dígitos (apenas celulares)');
      } else {
        setPhoneError('Número inválido. Digite um telefone válido.');
      }
      return;
    }

    if (formData.phones.includes(fullPhone)) {
      setPhoneError('Este número já foi adicionado neste usuário');
      return;
    }

    setFormData((prev) => ({ ...prev, phones: [...prev.phones, fullPhone] }));
    setPhoneInput('');
    setPhoneError('');
  };

  const handleRemovePhone = (phoneToRemove: string) => {
    setFormData((prev) => ({ ...prev, phones: prev.phones.filter((p) => p !== phoneToRemove) }));
  };

  const canManageUsers = currentUser?.roles.some((r) => r === 'admin' || r === 'gestor');
  const isCurrentUserAdmin = currentUser?.roles.some((r) => r === 'admin') || false;
  const creatableRoles = isCurrentUserAdmin
    ? ['admin', 'gestor', 'colaborador']
    : ['colaborador'];

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, clientsData, damsData] = await Promise.all([
        userService.getAllUsers(),
        clientService.getAllClients(),
        damService.getAllDams(),
      ]);
      setUsers(usersData);
      setClients(clientsData);
      setDams(damsData);
    } catch (err) {
      setError('Erro ao carregar dados');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userService, clientService, damService, setUsers, setClients, setDams, setLoading, setError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (phoneInput.trim()) {
      handleAddPhone();
      return;
    }

    const phoneNumbersCount = formData.phones.length;

    if (phoneNumbersCount > MAX_USER_PHONE_NUMBERS) {
      setError(`Usuário pode ter no máximo ${MAX_USER_PHONE_NUMBERS} números de telefone`);
      return;
    }

    if (editingUser) {
      if (!formData.name || !formData.name.trim()) {
        setError('Informe o nome do usuário');
        return;
      }
    } else {
      if (!formData.email || !formData.name || !formData.clientId || !formData.roleName) {
        setError('Preencha todos os campos obrigatórios');
        return;
      }

      if (!creatableRoles.includes(formData.roleName)) {
        setError('Você não tem permissão para criar usuário com esta role');
        return;
      }
    }

    try {
      setLoading(true);
      
      if (editingUser) {
        const updated = await userService.updateUser(editingUser.id, {
          name: formData.name.trim(),
          phoneNumber: formData.phones.length > 0 ? formData.phones.join(', ') : undefined,
          damIds: formData.damIds.length > 0 ? formData.damIds : undefined,
        });
        setUsers(users.map((u: IUser) => (u.id === updated.id ? updated : u)));
      } else {
        const created = await userService.createUser({
          email: formData.email.trim(),
          name: formData.name.trim(),
          phoneNumber: formData.phones.length > 0 ? formData.phones.join(', ') : undefined,
          clientId: formData.clientId,
          roleNames: [formData.roleName],
          damIds: formData.damIds.length > 0 ? formData.damIds : undefined,
        });
        setUsers([...users, created]);
        setSuccessMessage(`Convite enviado para ${created.email}. O usuário definirá a própria senha por e-mail.`);
      }
      
      setFormData({ email: '', name: '', phones: [], clientId: '', roleName: 'colaborador', damIds: [] });
      setPhoneInput('');
      setPhoneError('');
      setSelectedCountryCode('+55');
      setEditingUser(null);
      setShowForm(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar usuário';
      setError(errorMessage);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: IUser) => {
    setEditingUser(user);
    const parsedPhones = user.phoneNumber
      ? user.phoneNumber.split(',').map((item) => item.trim()).filter((item) => item.length > 0)
      : [];

    setFormData({
      email: user.email,
      name: user.name ?? '',
      phones: parsedPhones,
      clientId: user.clientId,
      roleName: user.roles?.[0] || 'colaborador',
      damIds: user.dams?.map(d => d.id) || [],
    });
    setPhoneInput('');
    setPhoneError('');
    setSelectedCountryCode('+55');
    setShowForm(true);
  };

  const handleDelete = (user: IUser) => {
    setUserToDelete(user);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete || deleteConfirmText !== 'DELETAR') return;

    try {
      setLoading(true);
      await userService.deleteUser(userToDelete.id);
      removeUser(userToDelete.id);
      setShowDeleteModal(false);
      setUserToDelete(null);
      setDeleteConfirmText('');
    } catch (err) {
      setError('Erro ao deletar usuário');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setDeleteConfirmText('');
  };

  const handleToggleActive = async (user: IUser) => {
    try {
      setLoading(true);
      const updated = await userService.toggleUserActive(user.id, !user.isActive);
      setUsers(users.map((u: IUser) => (u.id === updated.id ? updated : u)));
    } catch (err) {
      setError('Erro ao atualizar status do usuário');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvite = async (user: IUser) => {
    try {
      setLoading(true);
      const response = await userService.resendInvite(user.id);
      setSuccessMessage(response.message);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao reenviar convite';
      setError(errorMessage);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({ email: '', name: '', phones: [], clientId: '', roleName: 'colaborador', damIds: [] });
    setPhoneInput('');
    setPhoneError('');
    setSelectedCountryCode('+55');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterClientId('all');
    setFilterStatus('all');
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch = !searchTerm ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClient = !filterClientId || filterClientId === 'all' || user.clientId === filterClientId;
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && user.isActive) ||
      (filterStatus === 'inactive' && !user.isActive);
    return matchesSearch && matchesClient && matchesStatus;
  });

  const getClientName = (clientId: string) => {
    return clients.find(c => c.id === clientId)?.name || 'Desconhecido';
  };

  const availableDamsForForm = formData.clientId
    ? dams.filter((dam) => dam.clientId === formData.clientId)
    : [];

  const handleDamToggle = (damId: string) => {
    setFormData(prev => ({
      ...prev,
      damIds: prev.damIds.includes(damId)
        ? prev.damIds.filter(id => id !== damId)
        : [...prev.damIds, damId],
    }));
  };

  if (usersLoading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando usuários...</p>
        </div>
      </div>
    );
  }

  if (!canManageUsers) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p>Acesso negado. Apenas administradores e gestores podem acessar esta página.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="h-8 w-8 text-blue-600" />
            Gerenciamento de Usuários
          </h1>
          <p className="text-slate-600 mt-2">Gerencie usuários e permissões do sistema</p>
        </div>
        <Button onClick={() => setShowForm(true)} disabled={usersLoading} className="h-11">
          <UserPlus className="h-5 w-5 mr-2" />
          Novo Usuário
        </Button>
      </div>

      {/* Alertas/Erros */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setError(null)}>✕</Button>
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="h-4 w-4" />
            <span>{successMessage}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setSuccessMessage(null)}>✕</Button>
        </div>
      )}

      {/* Filtros */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-slate-900">Filtros de Pesquisa</h2>
          </div>
          {(searchTerm || (filterClientId && filterClientId !== 'all') || filterStatus !== 'all') && (
            <Button onClick={clearFilters} variant="outline" size="sm" className="text-slate-600">
              Limpar filtros
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Label htmlFor="searchTerm" className="text-sm font-medium text-slate-700">
              Pesquisar
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="searchTerm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome ou e-mail..."
                className="h-11 pl-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="filterClient" className="text-sm font-medium text-slate-700">
              Filtrar por Cliente
            </Label>
            <Select value={filterClientId} onValueChange={setFilterClientId}>
              <SelectTrigger id="filterClient" className="h-11">
                <SelectValue placeholder="Todos os clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os clientes</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="filterStatus" className="text-sm font-medium text-slate-700">
              Filtrar por Status
            </Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger id="filterStatus" className="h-11">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="inactive">Inativos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Tabela de Usuários */}
      <Card>
        <CardContent className="p-6">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">
                {searchTerm || filterClientId || filterStatus !== 'all'
                  ? 'Nenhum usuário encontrado com os filtros aplicados'
                  : 'Nenhum usuário encontrado'}
              </p>
            </div>
          ) : (
            <div className="rounded-lg border max-h-[62vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Telefone</TableHead>
                    <TableHead className="font-semibold">Cliente</TableHead>
                    <TableHead className="font-semibold">Barragens</TableHead>
                    <TableHead className="font-semibold">Roles</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="text-right font-semibold">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{user.name || '-'}</TableCell>
                      <TableCell className="text-slate-600">{user.email}</TableCell>
                      <TableCell className="text-slate-600">{user.phoneNumber || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{getClientName(user.clientId)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{user.dams?.length || 0}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {user.roles && user.roles.length > 0 ? (
                            user.roles.map((role) => (
                              <Badge key={role} variant="default">{role}</Badge>
                            ))
                          ) : (
                            <Badge variant="secondary">colaborador</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant={user.isActive ? "default" : "secondary"}
                          size="sm"
                          onClick={() => handleToggleActive(user)}
                          disabled={usersLoading}
                        >
                          {user.isActive ? (
                            <><CheckCircle2 className="h-3 w-3 mr-1" />Ativo</>
                          ) : (
                            <><XCircle className="h-3 w-3 mr-1" />Inativo</>
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {!user.isActive && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResendInvite(user)}
                              disabled={usersLoading}
                              title="Reenviar convite"
                            >
                              <Mail className="h-4 w-4 text-blue-600" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(user)}
                            disabled={usersLoading}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(user)}
                            disabled={usersLoading}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingUser ? (
                <>
                  <Edit2 className="h-5 w-5 text-blue-600" />
                  Editar Usuário
                </>
              ) : (
                <>
                  <UserPlus className="h-5 w-5 text-blue-600" />
                  Novo Usuário
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingUser && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                O usuário será criado sem senha. Um convite será enviado por e-mail para que ele defina a própria senha.
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email {!editingUser && '*'}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                    className="h-11 pl-10"
                    disabled={editingUser !== null}
                    required={!editingUser}
                  />
                </div>
                {editingUser && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Email não pode ser alterado
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                  Nome *
                </Label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nome completo"
                    className="h-11 pl-10"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                  Telefones ({formData.phones.length}/{MAX_USER_PHONE_NUMBERS})
                </Label>
                {formData.phones.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {formData.phones.map((phone, index) => {
                      const { iso, code, number } = parsePhoneNumber(phone);
                      return (
                        <div key={index} className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2 min-w-[100px]">
                            <CountryFlag iso={iso} />
                            <span className="text-sm font-semibold text-blue-700">{code}</span>
                          </div>
                          <div className="h-6 w-px bg-blue-300"></div>
                          <Phone className="h-4 w-4 text-blue-600" />
                          <span className="flex-1 text-sm font-medium text-slate-700">{number}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemovePhone(phone)}
                            className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Select value={selectedCountryCode} onValueChange={setSelectedCountryCode}>
                      <SelectTrigger className="w-[130px] h-11">
                        <SelectValue>
                          <div className="flex items-center gap-2">
                            <CountryFlag iso={countries.find(c => c.code === selectedCountryCode)?.iso || 'br'} />
                            <span className="font-medium">{selectedCountryCode}</span>
                          </div>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            <div className="flex items-center gap-2">
                              <CountryFlag iso={country.iso} />
                              <span className="text-sm">{country.name}</span>
                              <span className="text-slate-500 text-xs font-medium">{country.code}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Input
                      id="phone"
                      type="tel"
                      value={formatPhoneNumber(phoneInput, selectedCountryCode)}
                      onChange={(e) => {
                        const numbers = e.target.value.replace(/\D/g, '');
                        setPhoneInput(numbers);
                        setPhoneError('');
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddPhone();
                        }
                      }}
                      placeholder={countries.find(c => c.code === selectedCountryCode)?.placeholder || '11987654321'}
                      className="flex-1 h-11"
                      disabled={formData.phones.length >= MAX_USER_PHONE_NUMBERS}
                    />
                  </div>

                  <Button
                    type="button"
                    onClick={handleAddPhone}
                    variant="outline"
                    className="w-full h-10"
                    disabled={formData.phones.length >= MAX_USER_PHONE_NUMBERS}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {formData.phones.length >= MAX_USER_PHONE_NUMBERS ? 'Limite de telefones atingido' : 'Adicionar Telefone'}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Até {MAX_USER_PHONE_NUMBERS} números por usuário.
                </p>
                {phoneError && (
                  <p className="text-xs text-red-600 mt-1">{phoneError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="client" className="text-sm font-medium text-slate-700">
                  Cliente *
                </Label>
                <Select
                  value={formData.clientId}
                  onValueChange={(value) => setFormData({ ...formData, clientId: value, damIds: [] })}
                  disabled={editingUser !== null}
                >
                  <SelectTrigger id="client" className="h-11">
                    <SelectValue placeholder="Selecione um cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editingUser && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Cliente não pode ser alterado
                  </p>
                )}
              </div>
            </div>

            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="role" className="text-sm font-medium text-slate-700">
                  Role *
                </Label>
                <Select
                  value={formData.roleName}
                  onValueChange={(value) => setFormData({ ...formData, roleName: value })}
                >
                  <SelectTrigger id="role" className="h-11">
                    <SelectValue placeholder="Selecione a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {creatableRoles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!isCurrentUserAdmin && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Gestores podem criar apenas colaboradores
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">
                Barragens (máximo 5)
              </Label>
              <div className="grid grid-cols-2 gap-2 p-4 border rounded-lg max-h-40 overflow-y-auto">
                {availableDamsForForm.map(dam => (
                  <label key={dam.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.damIds.includes(dam.id)}
                      onChange={() => handleDamToggle(dam.id)}
                      disabled={formData.damIds.length >= 5 && !formData.damIds.includes(dam.id)}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm">{dam.name}</span>
                  </label>
                ))}
                {!formData.clientId && (
                  <p className="text-sm text-slate-500 col-span-2">Selecione um cliente para listar as barragens.</p>
                )}
                {formData.clientId && availableDamsForForm.length === 0 && (
                  <p className="text-sm text-slate-500 col-span-2">Nenhuma barragem disponível para este cliente.</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={handleCancel} className="h-11">
                Cancelar
              </Button>
              <Button type="submit" disabled={usersLoading} className="h-11">
                {usersLoading ? 'Salvando...' : editingUser ? 'Salvar' : 'Criar e Enviar Convite'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <DialogTitle className="text-xl">Deletar Usuário</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium mb-2">
                ⚠️ Esta ação é irreversível!
              </p>
              <p className="text-sm text-red-700">
                Você está prestes a deletar o usuário:{' '}
                <span className="font-semibold">{userToDelete?.name}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-delete" className="text-sm font-medium text-slate-700">
                Para confirmar, digite <span className="font-bold text-red-600">DELETAR</span>:
              </Label>
              <Input
                id="confirm-delete"
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Digite DELETAR"
                className="h-11"
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={cancelDelete} 
              className="h-11"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              disabled={deleteConfirmText !== 'DELETAR' || usersLoading}
              className="h-11 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {usersLoading ? 'Deletando...' : 'Deletar Usuário'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
